<script setup>
import {computed, onMounted, ref} from "vue";
import {VueTelInput} from "vue-tel-input";

import useHelpers from "@/Composables/UseHelpers";
const {$t} = useHelpers();

const v_tel_input = ref()
const participant_tel_no_model = ref()

const minExpiryDate = new Date(+new Date + 12096e5).toJSON().split('T')[0] // today + 14days

const emit = defineEmits([
    'update:participant_name',
    'update:participant_id_type',
    'update:participant_id',
    'update:participant_tel_no',
    'update:participant_email',
    'update:mandate_period',
]);

const props = defineProps({
  participant_name: {
    required: true,
  },
  participant_id_type: {
    required: false,
    default: null
  },
  participant_id: {
    required: false,
    default: null
  },
  participant_tel_no: {
    required: true,
  },
  participant_email: {
    required: true,
  },
  mandate_period: {
    required: true,
  },
  mandate_enabled: {
    required: false,
    default: false,
  },
  mandate_application_type: {
    required: false,
    default: null
  },
  recurring_transaction_enabled: {
    required: false,
    default: false
  },
  participant_email_required: {
    required: false,
    default: false
  },
  id_types: {
    required: false,
    default: {}
  },
  mandate_period_enabled: {
    required: false,
    default: false
  },
  mandate_period_options_data: {
    required: true,
    default: []
  },
  errors: {
    required: true,
  }
});

const emailRequired = computed(() =>
    props.recurring_transaction_enabled == 1 ||
    props.mandate_enabled == 1 ||
    (props.participant_email_required ? true : false)
);

const emailPlaceholder = computed(() => {
  if (props.mandate_enabled == 1) {
    return $t('donation-form.email_max_length');
  } else if (props.recurring_transaction_enabled == 1 || props.mandate_enabled == 1 || props.participant_email_required) {
    return '';
  } else {
    return 'Kosongkan jika tiada emel...'
  }
});

function disableKeydown (e) {
    e.preventDefault();
}

onMounted(() => {
  participant_tel_no_model.value = props.participant_tel_no;
})
</script>

<template>
  <!-- Full Name -->
  <div class="form-group row mb-2">
    <label class="col-4 col-form-label" for="participant_name">
      {{ $t('donation-form.full_name') }}

      <!-- Required -->
      <span class="text-red"> *</span>
    </label>
    <div class="col-8">
      <input
        type="text"
        id="participant_name"
        name="participant_name"
        required="required"
        class="form-control"
        :class="{ 'is-invalid': errors.participant_name }"
        :disabled="mandate_enabled && mandate_application_type !== '01'"
        :value="participant_name"
        @input="emit('update:participant_name', $event.target.value)"
      >

      <!-- Error message -->
      <div class="invalid-feedback" v-if="errors.participant_name">
        {{ errors.participant_name }}
      </div>
    </div>
  </div>

  <!-- ID Number -->
  <div class="form-group row mb-2" v-if="mandate_enabled">
    <label class="col-4 col-form-label" for="participant_id">
      {{ $t('donation-form.donor_id') }}

      <!-- Required -->
      <span class="text-red"> *</span>
    </label>
    <div class="col-8">
      <div class="input-group">
        <div class="input-group-prepend">
          <select
            id="participant_id_type"
            name="participant_id_type"
            class="form-control"
            style="background: #e9ecef;"
            :disabled="mandate_enabled && mandate_application_type !== '01'"
            :value="participant_id_type"
            @input="emit('update:participant_id_type', $event.target.value)"
          >
            <template v-for="(type, index) in id_types">
              <option :value="index">
                {{ type }}
              </option>
            </template>
          </select>
        </div>

        <input
          type="text"
          id="participant_id"
          name="participant_id"
          class="form-control"
          :class="{ 'is-invalid': errors.participant_id }"
          required="required"
          :disabled="mandate_enabled && mandate_application_type !== '01'"
          :value="participant_id"
          @input="emit('update:participant_id', $event.target.value)"
        >
      </div>

      <!-- Error message -->
      <div class="invalid-feedback" v-if="errors.participant_id">
        {{ errors.participant_id }}
      </div>
    </div>
  </div>

  <!-- Mandate Period -->
  <div class="form-group row mb-2" v-if="mandate_enabled && mandate_period_enabled">
    <label class="col-4 col-form-label" for="mandate_period">
      {{ $t('donation-form.mandate_period') }}

      <!-- Required -->
      <span class="text-red"> *</span>
    </label>
    <div class="col-8">
      <select
          id="mandate_period"
          name="mandate_period"
          class="form-control"
          placeholde=""
          :disabled="mandate_enabled && mandate_application_type !== '01'"
          :value="mandate_period"
          @input="emit('update:mandate_period', $event.target.value)"
      >
        <template v-for="months in mandate_period_options_data">
          <option :value="months">
            {{ months }} {{ $t('donation-form.month')}}
          </option>
        </template>
      </select>

      <!-- Error message -->
      <div class="invalid-feedback" v-if="errors.mandate_period">
        {{ errors.mandate_period }}
      </div>
    </div>
  </div>

  <!-- Mobile Number -->
  <div class="form-group row mb-2" id="hp_no_iti_wrapper">
    <label class="col-4 col-form-label" for="participant_tel_no">
      {{ $t('donation-form.mobile_no') }}

      <!-- Required -->
      <span class="text-red"> *</span>
    </label>
    <div class="col-8">
      <vue-tel-input
          :class="{
            'form-control': true,
            'is-invalid': errors.participant_tel_no ? true : false
          }"
          :input-options="{
            placeholder: '169164454',
            maxlength: 16,
            showDialCode: false,
            autoFormat: false,
            type: 'tel',
            id: 'participant_tel_no',
          }"
          :dropdown-options="{
            showDialCodeInSelection: true,
            showDialCodeInList: true,
            showFlags: true,
          }"
          :disabled="mandate_enabled == 1 && mandate_application_type === '03'"
          v-model="participant_tel_no_model"
          ref="v_tel_input"
          @input="emit('update:participant_tel_no', $event.target.value)"
      />

      <!-- Error message -->
      <div class="invalid-feedback" v-if="errors.participant_tel_no">
        {{ errors.participant_tel_no }}
      </div>
    </div>
  </div>

  <!-- Email -->
  <div class="form-group row mb-2">
    <label class="col-4 col-form-label" for="participant_email">
      {{ $t('donation-form.email') }}

      <!-- Required -->
      <template v-if="emailRequired">
        <span class="text-red"> *</span>
      </template>
    </label>
    <div class="col-8">
      <input
        type="email"
        inputmode="email"
        id="participant_email"
        name="participant_email"
        required="required"
        class="form-control"
        :class="{ 'is-invalid': errors.participant_email }"
        :placeholder="emailPlaceholder"
        :disabled="mandate_enabled && mandate_application_type === '03'"
        :maxlength="mandate_enabled ? 27 : 150"
        :value="participant_email"
        @input="emit('update:participant_email', $event.target.value)"
      >

      <!-- Error message -->
      <div class="invalid-feedback" v-if="errors.participant_email">
        {{ errors.participant_email }}
      </div>
    </div>
  </div>
</template>
